import { useRef, useState } from "react";
import { Button, Card, Progress, Typography, message } from "antd";
import ImageCropperModal from "./ImageCropperModal";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  GlobalOutlined,
  LoadingOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import SidePromotionSection from "components/promotions/SidePromotionSection";

const { Paragraph, Text, Title } = Typography;

// page shell
export function WizardPageShell({ eyebrow, title, description, onGenerate, generating, children }) {
  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon">
              <GlobalOutlined />
            </span>
            <div>
              <Text className="olreadia-flow-eyebrow">{eyebrow}</Text>
              <Title level={2}>{title}</Title>
              <Paragraph>{description}</Paragraph>
            </div>
            <Button type="primary" icon={<SaveOutlined />} loading={generating} onClick={onGenerate}>
              Generate
            </Button>
          </section>
          {children}
        </main>
        <SidePromotionSection className="olreadia-flow-promotions" />
      </div>
    </div>
  );
}

// presets
export const COLOR_PRESETS = [
  { primary: "#27ae30", secondary: "#C9F2CB" },
  { primary: "#1a56db", secondary: "#C9D9F2" },
  { primary: "#e11d48", secondary: "#F2C9CF" },
  { primary: "#f59e0b", secondary: "#F2E8C9" },
  { primary: "#8b5cf6", secondary: "#E2C9F2" },
  { primary: "#0891b2", secondary: "#C9EAF2" },
  { primary: "#059669", secondary: "#C9F2E5" },
  { primary: "#dc2626", secondary: "#F2C9C9" },
  { primary: "#1d4ed8", secondary: "#C9D9F2" },
  { primary: "#7c3aed", secondary: "#DEC9F2" },
  { primary: "#db2777", secondary: "#F2C9E2" },
  { primary: "#ea580c", secondary: "#F2D9C9" },
  { primary: "#215C8B", secondary: "#C9DDF2" },
  { primary: "#8B2121", secondary: "#F2C9C9" },
  { primary: "#C76B1C", secondary: "#F6DEC7" },
];

export const STAT_PRESETS = {
  "Clients Served": { label: "Happy Clients", suffix: "+", placeholder: "500" },
  "Years in Business": { label: "Years Experience", suffix: "+", placeholder: "15" },
  "Projects Completed": { label: "Projects Completed", suffix: "+", placeholder: "120" },
  "5-Star Reviews": { label: "5-Star Reviews", suffix: "+", placeholder: "200" },
  "Satisfaction Rate": { label: "Satisfaction Rate", suffix: "%", placeholder: "98" },
  "Awards Won": { label: "Awards Won", suffix: "+", placeholder: "12" },
  "Countries Served": { label: "Countries Served", suffix: "+", placeholder: "10" },
  "Team Members": { label: "Team Members", suffix: "+", placeholder: "25" },
  "Response Time": { label: "Avg Response Time", suffix: "hr", placeholder: "2" },
  "Uptime %": { label: "Uptime", suffix: "%", placeholder: "99.9" },
};

export const SKILL_LEVELS = ["Beginner", "Intermediate", "Advanced", "Expert"];

export const SKILL_CATEGORIES = [
  "Design",
  "Development",
  "Marketing",
  "Writing",
  "Photography",
  "Video",
  "Music",
  "Business",
  "Languages",
  "Tools",
  "Other",
];

// helpers
export const hasValue = (value) =>
  typeof value === "string" ? value.trim().length > 0 : value != null;

export const firstValue = (...values) => values.find(hasValue) ?? "";

export const firstArray = (...values) =>
  values.find((v) => Array.isArray(v) && v.length > 0) ?? [];

// form field
export function FormField({ label, required, hint, children }) {
  return (
    <div className="wiz-field">
      <div className="wiz-field__label-row">
        <span className="wiz-field__label">
          {label}
          {required && <span className="wiz-field__required"> *</span>}
        </span>
      </div>
      {hint ? <div className="wiz-field__hint">{hint}</div> : null}
      <div className="wiz-field__control">{children}</div>
    </div>
  );
}


// uploadFn: async (file, slotType, onProgress) => url
export function useImageUpload(uploadFn) {
  const [uploadingKey, setUploadingKey] = useState(null);
  const [progress, setProgress] = useState(0);

  const upload = async (key, file, slotType, onSuccess) => {
    setUploadingKey(key);
    setProgress(0);
    try {
      const url = await uploadFn(file, slotType, (pct) => setProgress(pct));
      onSuccess(url);
    } catch (err) {
      message.error(err?.message || "Image upload failed.");
    } finally {
      setUploadingKey(null);
      setProgress(0);
    }
  };

  return { uploadingKey, progress, upload };
}

// image uploader
export function ImageUploadBox({
  fieldKey,
  slotType = "GALLERY",
  value,
  onUpload,
  onRemove,
  uploadingKey,
  progress = 0,
  label,
  hint,
  aspectLabel,
  height = 160,
  circle = false,
}) {
  const [dragging, setDragging] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropFileName, setCropFileName] = useState("image.jpg");
  const fileRef = useRef(null);
  const isUploading = uploadingKey === fieldKey;

  // step 1: file chosen (via click or drop) → open the cropper, don't upload yet
  const handleFile = (file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    setCropFileName(file.name);
    setCropSrc(URL.createObjectURL(file));
  };

  // step 2: crop confirmed - hand the cropped File to the caller's uploader
  const handleCropConfirm = (croppedFile) => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    onUpload(croppedFile);
  };

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (isUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <FormField label={label} hint={hint}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!isUploading) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !isUploading && fileRef.current?.click()}
        className={`wiz-image-upload${circle ? " wiz-image-upload--circle" : ""}${
          dragging ? " is-dragging" : ""
        }`}
        style={{ height, ...(circle ? { width: height } : {}) }}
      >
        {value ? (
          <>
            <img src={value} alt="" className="wiz-image-upload__img" />
            <div className="wiz-image-upload__overlay">
              {isUploading ? (
                <div className="wiz-image-upload__progress">
                  <div className="wiz-image-upload__progress-track">
                    <div
                      className="wiz-image-upload__progress-bar"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span>{progress}%</span>
                </div>
              ) : (
                <span>Tap to change</span>
              )}
            </div>
          </>
        ) : (
          <div className="wiz-image-upload__empty">
            {isUploading ? (
              <>
                <LoadingOutlined style={{ fontSize: 20 }} />
                <span>Uploading… {progress}%</span>
              </>
            ) : (
              <>
                <PlusOutlined style={{ fontSize: 20 }} />
                <span>Drop image or click to browse</span>
                <span className="wiz-image-upload__sub">{aspectLabel}</span>
              </>
            )}
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = "";
          }}
        />
      </div>
      {value && !isUploading ? (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={onRemove}
          className="wiz-image-upload__remove"
        >
          Remove image
        </Button>
      ) : null}

      {/* cropper modal — rendered outside the dropzone div to avoid z-index issues */}
      {cropSrc ? (
        <ImageCropperModal
          imageSrc={cropSrc}
          fileName={cropFileName}
          slotType={slotType}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      ) : null}
    </FormField>
  );
}

// color picker
export function ColorPickerField({ primaryColor, secondaryColor, onChangePrimary }) {
  return (
    <FormField label="Brand Color" hint="Used for buttons, accents and headings">
      <div className="wiz-color-grid">
        {COLOR_PRESETS.map(({ primary, secondary }) => (
          <button
            type="button"
            key={primary}
            className={`wiz-color-swatch${primaryColor === primary ? " is-active" : ""}`}
            style={{ backgroundColor: primary }}
            onClick={() => onChangePrimary(primary, secondary)}
          >
            {primaryColor === primary ? "✓" : ""}
          </button>
        ))}
      </div>
      <div className="wiz-color-custom-row">
        <span className="wiz-color-dot" style={{ backgroundColor: primaryColor || "#e5e7eb" }} />
        <input
          className="wiz-color-hex-input"
          value={primaryColor}
          maxLength={7}
          onChange={(e) => {
            const v = e.target.value;
            const match = COLOR_PRESETS.find((p) => p.primary === v);
            onChangePrimary(v, match ? match.secondary : secondaryColor);
          }}
          placeholder="#000000"
        />
      </div>
      {secondaryColor ? (
        <div className="wiz-color-secondary-row">
          <span className="wiz-color-dot" style={{ backgroundColor: secondaryColor }} />
          <span className="wiz-color-secondary-label">Secondary: {secondaryColor}</span>
        </div>
      ) : null}
    </FormField>
  );
}

// progress header
export function ProgressHeader({ steps, currentStep, onBack }) {
  const step = steps[currentStep];
  const percent = Math.round(((currentStep + 1) / steps.length) * 100);

  return (
    <div className="wiz-progress-header">
      <div className="wiz-progress-header__top">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          className="wiz-progress-header__back"
        />
        <div className="wiz-progress-header__center">
          <span className="wiz-progress-header__eyebrow">
            Step {currentStep + 1} of {steps.length}
          </span>
          <span className="wiz-progress-header__title">
            {step.icon} {step.label}
          </span>
        </div>
        <div className="wiz-progress-header__dots">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`wiz-progress-header__dot${i < currentStep ? " is-done" : ""}${
                i === currentStep ? " is-active" : ""
              }`}
            />
          ))}
        </div>
      </div>
      <Progress percent={percent} showInfo={false} strokeColor="#27ae30" size="small" />
    </div>
  );
}

// repeatable section
export function RepeatableSection({
  items,
  onChange,
  renderItem,
  emptyItem,
  addLabel,
  emptyLabel,
  itemLabel = "Item",
  max,
}) {
  const addItem = () => {
    if (max && items.length >= max) return;
    onChange([...items, { ...emptyItem }]);
  };
  const updateItem = (index, patch) => {
    onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };
  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="wiz-repeatable">
      {items.map((item, index) => (
        <Card
          key={index}
          size="small"
          className="wiz-card"
          title={`${itemLabel} ${index + 1}`}
          extra={
            <Button type="link" danger size="small" onClick={() => removeItem(index)}>
              Remove
            </Button>
          }
        >
          {renderItem(item, (patch) => updateItem(index, patch), index)}
        </Card>
      ))}
      {!max || items.length < max ? (
        <button type="button" className="wiz-add-btn" onClick={addItem}>
          <PlusOutlined /> {items.length === 0 ? addLabel.first : addLabel.more}
        </button>
      ) : null}
      {items.length === 0 && emptyLabel ? (
        <div className="wiz-empty-note">{emptyLabel}</div>
      ) : null}
    </div>
  );
}

// stats step
export function StatsStep({ stats, setStats }) {
  const availableTypes = Object.keys(STAT_PRESETS).filter(
    (type) => !stats.find((s) => s.type === type),
  );

  const addStat = (type) => {
    if (stats.length >= 4) return;
    const preset = STAT_PRESETS[type];
    setStats([...stats, { type, value: "", suffix: preset.suffix, label: preset.label }]);
  };
  const updateStat = (index, patch) =>
    setStats(stats.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const removeStat = (index) => setStats(stats.filter((_, i) => i !== index));

  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        Pick up to 4 stats that best represent you. These appear as highlight numbers on your
        website.
      </div>

      {stats.map((stat, index) => (
        <Card
          key={index}
          size="small"
          className="wiz-card"
          title={stat.type}
          extra={
            <Button type="link" danger size="small" onClick={() => removeStat(index)}>
              Remove
            </Button>
          }
        >
          <div className="wiz-two-col">
            <FormField label="Value" required>
              <input
                className="wiz-plain-input"
                value={stat.value}
                onChange={(e) => updateStat(index, { value: e.target.value })}
                placeholder={STAT_PRESETS[stat.type]?.placeholder ?? "0"}
              />
            </FormField>
            <FormField label="Suffix">
              <input
                className="wiz-plain-input"
                value={stat.suffix}
                maxLength={3}
                onChange={(e) => updateStat(index, { suffix: e.target.value })}
              />
            </FormField>
          </div>
          <FormField label="Label">
            <input
              className="wiz-plain-input"
              value={stat.label}
              maxLength={30}
              onChange={(e) => updateStat(index, { label: e.target.value })}
            />
          </FormField>
        </Card>
      ))}

      {stats.length < 4 && availableTypes.length > 0 ? (
        <Card
          size="small"
          className="wiz-card"
          title={`Add a stat ${stats.length > 0 ? `(${4 - stats.length} remaining)` : ""}`}
        >
          <div className="wiz-chip-grid">
            {availableTypes.map((type) => (
              <button
                type="button"
                key={type}
                className="wiz-stat-chip"
                onClick={() => addStat(type)}
              >
                {type} <PlusOutlined />
              </button>
            ))}
          </div>
        </Card>
      ) : null}

      {stats.length === 0 ? (
        <div className="wiz-empty-note">
          No stats selected. Pick from the options above — these show as prominent numbers on
          your site.
        </div>
      ) : null}
    </div>
  );
}

// social links
export const SOCIAL_FIELDS = [
  { key: "facebook", icon: "🇫", label: "Facebook", placeholder: "https://facebook.com/yourbusiness" },
  { key: "instagram", icon: "📸", label: "Instagram", placeholder: "https://instagram.com/yourbusiness" },
  { key: "twitter", icon: "🐦", label: "Twitter / X", placeholder: "https://x.com/yourbusiness" },
  { key: "linkedin", icon: "💼", label: "LinkedIn", placeholder: "https://linkedin.com/company/yourbusiness" },
  { key: "youtube", icon: "▶️", label: "YouTube", placeholder: "https://youtube.com/@yourbusiness" },
];

export function SocialLinksFields({ data, onChange }) {
  return (
    <>
      {SOCIAL_FIELDS.map(({ key, icon, label, placeholder }) => (
        <div key={key} className="wiz-social-row">
          <span className="wiz-social-icon-wrap">{icon}</span>
          <div style={{ flex: 1 }}>
            <FormField label={label}>
              <input
                className="wiz-plain-input"
                value={data[key]}
                onChange={(e) => onChange(key, e.target.value)}
                placeholder={placeholder}
              />
            </FormField>
          </div>
        </div>
      ))}
    </>
  );
}